import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productEditorPath, productEditorSectionPath, productPath } from "@travily/routes";
import type ListingsEditorPriceAmount from "@travily/types/pages/ListingsEditorPriceAmount";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ProductPriceAmountEditor } from "./show.view";

type PageProps = ListingsEditorPriceAmount;

interface PriceAmountFormData {
  price_cents: string;
}

const ProductEditPriceAmountPage = (props: PageProps) => {
  const { listing_id } = props;
  const sections = buildEditorSections(props);
  const form = useForm<PriceAmountFormData>({ price_cents: props.price_cents ? String(props.price_cents / 100) : "" });

  const handleSave = () => {
    form.patch(productEditorSectionPath(listing_id, "price_amount"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="price_amount"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={productPath(listing_id)}
      href={productEditorPath(listing_id)}
      sections={sections}
      title={t("web.products.editors.show.title")}
    >
      <Head title={t("web.products.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ProductPriceAmountEditor
        currency={props.currency}
        error={firstError(form.errors.price_cents)}
        onChange={(value) => form.setData("price_cents", value)}
        platformFeeRate={props.platform_fee_rate}
        priceCents={form.data.price_cents ? Math.round(Number(form.data.price_cents) * 100) : null}
        value={form.data.price_cents}
      />
    </EditFrame>
  );
};

ProductEditPriceAmountPage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ProductEditPriceAmountPage;
